/**
 * Button Component
 * Modern with gradients and shadows
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className = '', variant = 'primary', size = 'md', isLoading, children, disabled, ...props }, ref) => {
    const baseStyles = `
      inline-flex items-center justify-center font-medium
      transition-all duration-200
      focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none
    `;

    const variants = {
      primary: `
        text-white
        bg-gradient-to-br from-primary-500 to-primary-600
        shadow-teal
        hover:shadow-teal-lg hover:-translate-y-0.5
      `,
      secondary: `
        bg-white border border-border text-text-primary
        hover:border-primary-500 hover:text-primary-600
        shadow-sm hover:shadow-md
      `,
      accent: `
        bg-primary-500 text-white
        hover:bg-primary-600
        shadow-sm hover:shadow-md
      `,
      ghost: `
        bg-transparent text-text-secondary
        hover:text-primary-600 hover:bg-primary-50
      `,
    };

    const sizes = {
      sm: 'px-4 py-2 text-small',
      md: 'px-6 py-3 text-base',
      lg: 'px-8 py-4 text-body-lg',
    };

    return (
      <button
        ref={ref}
        className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <span className="flex items-center">
            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
            <span>Loading</span>
          </span>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
